import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import stylesPc from "./ContentSearch.module.css";
import stylesSp from "./ContentSearch_SP.module.css";
import {
    Button,
    createStyles,
    Fab,
    FilledInput,
    InputAdornment,
    makeStyles,
    Modal,
    Slide,
    Theme,
} from "@material-ui/core";
import PuiTypography from "../../../components/elements/PuiTypography";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { search } from "../../../../src/viewer/search/SearchActions";
import { Checkbox, FormControlLabel, CircularProgress } from "@material-ui/core";
import { Search, Person, Clear } from "@material-ui/icons";
import { CounterObject, PresenterList, ShowResult, Tag } from "../Search";
import ContentSearchDetail from "./ContentSearchDetail";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import FilterListIcon from "@material-ui/icons/FilterList";

/**
|--------------------------------------------------
| サーチ画面
|--------------------------------------------------
*/

interface ContentSearchIF {
    changeOpenDrawerMenu?: Dispatch<SetStateAction<boolean>>;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        actionButton: {
            position: "fixed",
            bottom: "30px",
            right: "20px",
            zIndex: 200,
            backgroundColor: "var(--main)",
            color: "var(--white)",
            "&:hover": {
                backgroundColor: "var(--main)",
            },
        },
        checkBox: {
            "& .MuiIconButton-colorSecondary": {
                color: "var(--main)",
            },
            "& .MuiIconButton-colorSecondary:hover": {
                backgroundColor: "rgba(18,224,126,0.05)",
            },
        },
    })
);

export default function ContentSearch({ changeOpenDrawerMenu }: ContentSearchIF) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;
    const classes = useStyles();
    /* 変数の設定 */
    const [inputSearchWord, setInputSearchWord] = useState<string>("");
    const [isSlideOut, setIsSlideOut] = useState<boolean>(false);
    const [isOpenSearchModal, ShowSearchModal] = useState(false);
    const dispatch = useDispatch();
    const { result, isSearching } = useSelector((state: AppState) => ({
        result: state.search.result,
        isSearching: state.search.isSearching,
    }));

    //プレゼンターフィルタリング用
    const [presenterList, setPresenterList] = useState<PresenterList[]>([]);
    const [presenterContentCount, setPresenterContentCount] = useState<CounterObject>({});
    const [pIds, setPids] = useState<number[]>([]);

    const [isOpenSearchInput, changeOpenSearchInput] = useState<boolean>(false);

    //タグフィルタリング用
    const [tagList, setTagList] = useState<Tag[]>([]);
    const [tagContentCount, setTagContentCount] = useState<CounterObject>({});
    const [tIds, setTids] = useState<number[]>([]);

    //フィルタリングしたコンテンツリスト
    const [filteredResultList, setFilteredResultList] = useState<ShowResult[]>([]);

    //filter
    const [openFilter, setOpenFilter] = React.useState(false);

    //ユーザー情報
    const { userInfo } = useSelector((state: AppState) => ({
        userInfo: state.user.userInfo,
    }));

    const ResetSearch = () => {
        setPresenterList([]);
        setPresenterContentCount({});
        setPids([]);
        setTagList([]);
        setTagContentCount({});
        setTids([]);
        setFilteredResultList([]);
    };
    // Enterキーで検索
    const handleKeyPress = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && inputSearchWord.length > 0) {
                if (userInfo && inputSearchWord) {
                    //コンテンツ詳細情報取得
                    dispatch(search(userInfo?.id, inputSearchWord.split(" ")));
                    ShowSearchModal(true);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inputSearchWord]
    );

    // 検索ボタンを押下
    const handleSearch = useCallback(() => {
        if (inputSearchWord.length > 0) {
            if (userInfo && inputSearchWord) {
                //コンテンツ詳細情報取得
                dispatch(search(userInfo?.id, inputSearchWord.split(" ")));
                ShowSearchModal(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputSearchWord]);

    // 検索モーダルを開く
    const OpenSearchModal = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputSearchWord(event.target.value);
    };

    // 検索モーダルを閉じる
    const CloseSearchModal = () => {
        ShowSearchModal(false);
        ResetSearch();
        if (changeOpenDrawerMenu) changeOpenDrawerMenu(false);
    };

    /* プレゼンターチェックをチェック処理 */
    const handlePresenterCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        //チャックされたプレゼンターID
        let chkPresenterId = Number(event.target.value);
        if (!pIds.includes(chkPresenterId)) {
            const newPids = [...pIds];
            newPids.push(chkPresenterId);
            setPids(newPids);
        } else {
            const newPids = pIds.filter((id) => id !== chkPresenterId);
            setPids(newPids);
        }
    };

    /* タグをチェック処理 */
    const handleTagCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
        //チャックされたタグID
        let chkTagId = Number(event.target.value);
        if (!tIds.includes(chkTagId)) {
            const newTids = [...tIds];
            newTids.push(chkTagId);
            setTids(newTids);
        } else {
            const newTids = tIds.filter((id) => id !== chkTagId);
            setTids(newTids);
        }
    };

    //フィルタリング設定
    useEffect(() => {
        if (result) {
            setFilteredResultList(result);
        }
        /*  プレゼンターフィルタリング用 */
        let tempPresenterNames: PresenterList[] = [];
        let tempPresentContentCount: CounterObject = {};

        /* タグフィルタリング用*/
        let tempTagList: Tag[] = [];
        let tempTagContentCount: CounterObject = {};

        result.length !== 0 &&
            result.forEach((content) => {
                const isExistingPresenter = tempPresenterNames.find(
                    (presenter) => presenter.id === content.presenterId
                )
                    ? true
                    : false;

                //プレゼンター名を重複で表示しない様に判断
                if (!isExistingPresenter) {
                    //プレゼンター名とIDをリストに新しい追加
                    tempPresenterNames.push({
                        id: content.presenterId,
                        name: content.presenterName,
                    });
                    //プレゼンターに寄ってコンテンツ数を設定
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: 1,
                    };
                } else {
                    //プレゼンターはリストにある場合、コンテンツ数＋１
                    const contetnCountByPresenter = tempPresentContentCount[content.presenterId];
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: contetnCountByPresenter + 1,
                    };
                }

                content.tagList.forEach((tag: any) => {
                    const isExistingTag = tempTagList.find((tempTag) => tempTag.id === tag.id)
                        ? true
                        : false;

                    if (!isExistingTag) {
                        //フィルタリングタグリストにタグを追加
                        tempTagList.push({
                            id: tag.id,
                            title: tag.title,
                        });
                        //タグが付けたコンテンツ数の設定
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: 1,
                        };
                    } else {
                        //タグがリストにある場合、コンテンツ数+1
                        const contentCountByTag = tempTagContentCount[tag.id];
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: contentCountByTag + 1,
                        };
                    }
                });
            });

        //コンテンツ数降順でプレゼンターフィルタリング設定
        const sortedPresenterList = tempPresenterNames.sort((presenter1, presenter2) => {
            return tempPresentContentCount[presenter1.id] > tempPresentContentCount[presenter2.id]
                ? -1
                : 1;
        });

        // //コンテンツ数降順でタグリストフィルタリング設定
        const sortedTagList = tempTagList.sort((tag1, tag2) => {
            return tempTagContentCount[tag1.id] > tempTagContentCount[tag2.id] ? -1 : 1;
        });

        setPresenterList(sortedPresenterList);
        setPresenterContentCount(tempPresentContentCount);

        /*  タグフィルタリング*/
        setTagList(sortedTagList);
        setTagContentCount(tempTagContentCount);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [result]);

    //フィルタリング設定
    useEffect(() => {
        /*  プレゼンターフィルタリング用 */
        let tempPresenterNames: PresenterList[] = [];
        let tempPresentContentCount: CounterObject = {};

        /* タグフィルタリング用*/
        let tempTagList: Tag[] = [];
        let tempTagContentCount: CounterObject = {};

        filteredResultList.length !== 0 &&
            filteredResultList.forEach((content) => {
                const isExistingPresenter = tempPresenterNames.find(
                    (presenter) => presenter.id === content.presenterId
                )
                    ? true
                    : false;

                //プレゼンター名を重複で表示しない様に判断
                if (!isExistingPresenter) {
                    //プレゼンター名とIDをリストに新しい追加
                    tempPresenterNames.push({
                        id: content.presenterId,
                        name: content.presenterName,
                    });
                    //プレゼンターに寄ってコンテンツ数を設定
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: 1,
                    };
                } else {
                    //プレゼンターはリストにある場合、コンテンツ数＋１
                    const contetnCountByPresenter = tempPresentContentCount[content.presenterId];
                    tempPresentContentCount = {
                        ...tempPresentContentCount,
                        [content.presenterId]: contetnCountByPresenter + 1,
                    };
                }

                content.tagList.forEach((tag: any) => {
                    const isExistingTag = tempTagList.find((tempTag) => tempTag.id === tag.id)
                        ? true
                        : false;

                    if (!isExistingTag) {
                        //フィルタリングタグリストにタグを追加
                        tempTagList.push({
                            id: tag.id,
                            title: tag.title,
                        });
                        //タグが付けたコンテンツ数の設定
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: 1,
                        };
                    } else {
                        //タグがリストにある場合、コンテンツ数+1
                        const contentCountByTag = tempTagContentCount[tag.id];
                        tempTagContentCount = {
                            ...tempTagContentCount,
                            [tag.id]: contentCountByTag + 1,
                        };
                    }
                });
            });

        //コンテンツ数降順でプレゼンターフィルタリング設定
        const sortedPresenterList = tempPresenterNames.sort((presenter1, presenter2) => {
            return tempPresentContentCount[presenter1.id] > tempPresentContentCount[presenter2.id]
                ? -1
                : 1;
        });

        // //コンテンツ数降順でタグリストフィルタリング設定
        const sortedTagList = tempTagList.sort((tag1, tag2) => {
            return tempTagContentCount[tag1.id] > tempTagContentCount[tag2.id] ? -1 : 1;
        });

        setPresenterList(sortedPresenterList);
        setPresenterContentCount(tempPresentContentCount);

        /*  タグフィルタリング*/
        setTagList(sortedTagList);
        setTagContentCount(tempTagContentCount);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredResultList]);

    //表示データをフィルタリングする
    useEffect(() => {
        let tempFilterdResultList: ShowResult[] = [];
        if (pIds !== undefined && tIds !== undefined && pIds.length === 0 && tIds.length === 0) {
            tempFilterdResultList = [...result];
        } else {
            if (result) {
                if (pIds !== undefined && pIds.length !== 0) {
                    if (tIds !== undefined && tIds.length !== 0) {
                        pIds.forEach((pId) => {
                            filteredResultList.forEach((tempResult) => {
                                if (
                                    tempResult.presenterId === pId &&
                                    !tempFilterdResultList.includes(tempResult)
                                ) {
                                    tempFilterdResultList.push(tempResult);
                                }
                            });
                        });
                    } else {
                        pIds.forEach((pId) => {
                            result.forEach((tempResult) => {
                                if (
                                    tempResult.presenterId === pId &&
                                    !tempFilterdResultList.includes(tempResult)
                                ) {
                                    tempFilterdResultList.push(tempResult);
                                }
                            });
                        });
                    }
                } else {
                    if (tIds !== undefined && tIds.length !== 0) {
                        tIds.forEach((tId) => {
                            result.forEach((tempResult) => {
                                tempResult.tagList.forEach((tempTag) => {
                                    if (
                                        tempTag.id === tId &&
                                        !tempFilterdResultList.includes(tempResult)
                                    ) {
                                        tempFilterdResultList.push(tempResult);
                                    }
                                });
                            });
                        });
                    }
                }
            }
        }

        setFilteredResultList(tempFilterdResultList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pIds]);

    useEffect(() => {
        let tempFilterdResultList: ShowResult[] = [];
        if (pIds !== undefined && tIds !== undefined && pIds.length === 0 && tIds.length === 0) {
            tempFilterdResultList = [...result];
        } else {
            if (result) {
                if (tIds !== undefined && tIds.length !== 0) {
                    if (pIds !== undefined && pIds.length !== 0) {
                        tIds.forEach((tId) => {
                            filteredResultList.forEach((tempResult) => {
                                tempResult.tagList.forEach((tempTag) => {
                                    if (
                                        tempTag.id === tId &&
                                        !tempFilterdResultList.includes(tempResult)
                                    ) {
                                        tempFilterdResultList.push(tempResult);
                                    }
                                });
                            });
                        });
                    } else {
                        tIds.forEach((tId) => {
                            result.forEach((tempResult) => {
                                tempResult.tagList.forEach((tempTag) => {
                                    if (
                                        tempTag.id === tId &&
                                        !tempFilterdResultList.includes(tempResult)
                                    ) {
                                        tempFilterdResultList.push(tempResult);
                                    }
                                });
                            });
                        });
                    }
                } else {
                    if (pIds !== undefined && pIds.length !== 0) {
                        pIds.forEach((pId) => {
                            result.forEach((tempResult) => {
                                if (
                                    tempResult.presenterId === pId &&
                                    !tempFilterdResultList.includes(tempResult)
                                ) {
                                    tempFilterdResultList.push(tempResult);
                                }
                            });
                        });
                    }
                }
            }
        }

        setFilteredResultList(tempFilterdResultList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tIds]);

    const searchFilter = (
        <div className={styles.left}>
            {/* Presenterタイトルエリア */}
            <div className={styles.filterHeader}>
                <Person className={styles.personIcon} />
                <div className={styles.filterHeaderLable}>
                    <PuiTypography size={IsDevice("iphone") ? 14 : 16} bold={true}>
                        プレゼンター
                    </PuiTypography>
                </div>
            </div>
            {/* Presenterリストエリア */}
            <div
                className={styles.list}
                style={{
                    maxHeight: "40%",
                }}
            >
                {presenterList &&
                    presenterList?.length !== 0 &&
                    presenterList.map((presenter) => (
                        <FormControlLabel
                            key={presenter.id}
                            //name={`presenter${presenter.id}`}
                            className={classes.checkBox}
                            control={
                                <Checkbox
                                    icon={
                                        <img
                                            src={require("../../../assets/image/checkbox_white.svg")}
                                            alt={presenter.name}
                                        />
                                    }
                                    checkedIcon={
                                        <img
                                            src={require("../../../assets/image/checkbox_toggle_white.svg")}
                                            alt={presenter.name}
                                        />
                                    }
                                    id={String(presenter.id)}
                                    value={presenter.id}
                                    name={`presenterChk${presenter.id}`}
                                    onChange={handlePresenterCheck}
                                    checked={pIds.includes(presenter.id) ? true : false}
                                />
                            }
                            label={
                                <>
                                    <div className={styles.checkboxLable}>
                                        <div
                                            className={styles.filterCheckboxLable}
                                            data-for={presenter.name}
                                        >
                                            <PuiTypography>{presenter.name}</PuiTypography>
                                        </div>
                                        <div
                                            className={styles.contentCountLable}
                                            data-for={presenter.name}
                                        >
                                            <PuiTypography>
                                                {"("}
                                                {presenterContentCount[presenter.id]
                                                    ? presenterContentCount[presenter.id]
                                                    : 0}
                                                {")"}
                                            </PuiTypography>
                                        </div>
                                    </div>
                                </>
                            }
                        />
                    ))}
            </div>
            {/* タグタイトルエリア */}
            <div className={styles.filterHeader}>
                <img
                    src={require("../../../assets/image/tag.svg")}
                    alt="tag"
                    className={styles.tagIcon}
                />
                <div className={styles.filterHeaderLable}>
                    <PuiTypography size={IsDevice("iphone") ? 14 : 16} bold={true}>
                        タグ
                    </PuiTypography>
                </div>
            </div>
            {/* タグリストエリア */}
            <div
                className={styles.list}
                style={{
                    maxHeight: "40%",
                }}
            >
                {tagList &&
                    tagList?.length !== 0 &&
                    tagList.map((tag) => (
                        <FormControlLabel
                            key={tag.id}
                            name={`tag${tag.id}`}
                            className={classes.checkBox}
                            control={
                                <Checkbox
                                    icon={
                                        <img
                                            src={require("../../../assets/image/checkbox_white.svg")}
                                            alt={tag.title}
                                        />
                                    }
                                    checkedIcon={
                                        <img
                                            src={require("../../../assets/image/checkbox_toggle_white.svg")}
                                            alt={tag.title}
                                        />
                                    }
                                    id={String(tag.id)}
                                    value={tag.id}
                                    name={`presenterChk${tag.id}`}
                                    onChange={handleTagCheck}
                                    checked={tIds.includes(tag.id) ? true : false}
                                />
                            }
                            label={
                                <>
                                    <div className={styles.checkboxLable}>
                                        <div
                                            className={styles.filterCheckboxLable}
                                            data-for={tag.title}
                                        >
                                            <PuiTypography>{tag.title}</PuiTypography>
                                        </div>
                                        <div
                                            className={styles.contentCountLable}
                                            data-for={tag.title}
                                        >
                                            <PuiTypography>
                                                {"("}
                                                {tagContentCount[tag.id]
                                                    ? tagContentCount[tag.id]
                                                    : 0}
                                                {")"}
                                            </PuiTypography>
                                        </div>
                                    </div>
                                </>
                            }
                        />
                    ))}
            </div>
        </div>
    );

    return (
        <>
            {/* サーチ入力エリア */}

            {IsDevice("desktop") ? (
                isOpenSearchInput ? (
                    <FilledInput
                        placeholder="検索する"
                        id="searchInput"
                        value={inputSearchWord}
                        onChange={OpenSearchModal}
                        onKeyPress={handleKeyPress}
                        className={`${styles.searchInput} ${isSlideOut ? styles.slideOut : ""}`}
                        disableUnderline={true}
                        autoComplete={"off"}
                        autoFocus={true}
                        endAdornment={
                            <InputAdornment position="start">
                                <Button
                                    startIcon={
                                        <Search
                                            style={{
                                                color: "var(--white)",
                                            }}
                                        />
                                    }
                                    className={styles.searchButton}
                                    onClick={handleSearch}
                                ></Button>
                                <Button
                                    endIcon={
                                        <Clear
                                            style={{
                                                color: "var(--loading-bg)",
                                            }}
                                        />
                                    }
                                    className={styles.clearButton}
                                    onClick={() => {
                                        setIsSlideOut(true);
                                        setTimeout(() => {
                                            setIsSlideOut(false);
                                            changeOpenSearchInput(false);
                                            setInputSearchWord("");
                                        }, 400);
                                    }}
                                />
                            </InputAdornment>
                        }
                    ></FilledInput>
                ) : (
                    <InputAdornment
                        position="end"
                        onClick={() => changeOpenSearchInput(true)}
                        className={styles.searchOpenButton}
                    >
                        <Button
                            endIcon={
                                <Search
                                    style={{
                                        color: "var(--loading-bg)",
                                    }}
                                />
                            }
                        ></Button>
                    </InputAdornment>
                )
            ) : (
                <FilledInput
                    placeholder="検索する"
                    id="searchInput"
                    value={inputSearchWord}
                    onChange={OpenSearchModal}
                    onKeyPress={handleKeyPress}
                    className={styles.drawerSearchInput}
                    style={{ left: "-28px" }}
                    disableUnderline={true}
                    autoComplete={"off"}
                    endAdornment={
                        <InputAdornment position="start">
                            <Button
                                startIcon={
                                    <Search
                                        style={{
                                            fontFamily: "Yu Gothic",
                                            color: "var(--white)",
                                            marginTop: "-4px",
                                        }}
                                    />
                                }
                                className={styles.searchButton}
                                onClick={handleSearch}
                            ></Button>
                            <Button
                                endIcon={
                                    <Clear
                                        style={{
                                            color: "var(--loading-bg)",
                                            marginTop: "14px",
                                        }}
                                    />
                                }
                                className={styles.clearButton}
                                onClick={() => {
                                    changeOpenSearchInput(false);
                                    setInputSearchWord("");
                                }}
                            />
                        </InputAdornment>
                    }
                ></FilledInput>
            )}
            {/* サーチ画面モーダル */}
            <Modal
                className={styles.searchResultModal}
                aria-labelledby="spring-modal-title"
                aria-describedby="spring-modal-description"
                aria-hidden={false}
                open={isOpenSearchModal}
                onClose={CloseSearchModal}
                closeAfterTransition
            >
                <div className={styles.resultContainer}>
                    {/* 検索中にぐるぐるする */}
                    {isSearching && (
                        <div className={styles.noContent}>
                            <CircularProgress size={60} style={{ color: "var(--loading-bg)" }} />
                        </div>
                    )}
                    {/* 検索結果を表示する*/}
                    {!isSearching && (
                        <>
                            {/* 検索結果が０件の場合、 */}
                            {result && result.length === 0 && (
                                <>
                                    <div className={styles.noResult}>
                                        <PuiTypography size={17} bold={true}>
                                            「{inputSearchWord} 」と該当する検索結果はありません
                                        </PuiTypography>
                                    </div>
                                </>
                            )}
                            {/* 結果結果が１件以上の場合、 */}
                            {result && result.length !== 0 && (
                                <>
                                    {/* フィルタリングエリア */}
                                    {IsDevice("desktop") && searchFilter}
                                    <div className={styles.right}>
                                        {/*結果件数表示エリア */}
                                        <div className={styles.resultCountDiv}>
                                            <PuiTypography size={17} bold={true}>
                                                「{inputSearchWord} 」の検索結果（
                                                {filteredResultList &&
                                                filteredResultList.length !== 0
                                                    ? filteredResultList.length
                                                    : 0}
                                                ）
                                            </PuiTypography>
                                        </div>
                                        {filteredResultList && (
                                            <>
                                                {/* 検索結果表示*/}
                                                {filteredResultList.length !== 0 &&
                                                    filteredResultList.map((content) => (
                                                        <ContentSearchDetail
                                                            content={content}
                                                            key={content.id}
                                                            ShowSearchModal={ShowSearchModal}
                                                            changeOpenDrawerMenu={
                                                                changeOpenDrawerMenu
                                                            }
                                                        />
                                                    ))}
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {!IsDevice("desktop") && (
                        <>
                            <Fab
                                variant="circular"
                                size="small"
                                onClick={() => setOpenFilter(!openFilter)}
                                className={classes.actionButton}
                            >
                                {openFilter ? (
                                    <img
                                        src={require("../../../assets/image/closeFilter.svg")}
                                        alt="tag"
                                    />
                                ) : (
                                    <FilterListIcon />
                                )}
                            </Fab>
                            <Slide direction="left" in={openFilter}>
                                <div className={styles.searchFilter}>{searchFilter}</div>
                            </Slide>
                        </>
                    )}
                </div>
            </Modal>
        </>
    );
}
